/**
 * TimeTrack - State Store (Preact Signals)
 *
 * Zentraler State für die Anwendung.
 * Signals bieten granulare Reaktivität ohne Re-Renders.
 */

import { signal, computed } from '@preact/signals';
import type { TimeSheet, TimeEntry, Employee, TenantConfig } from '@timetrack/domain';
import type { SyncState, SyncStatus } from './types';

// =============================================================================
// Sync State
// =============================================================================

export const syncState = signal<SyncState>({
  status: 'idle',
  pendingCount: 0,
  lastSyncAt: null,
  error: null,
});

/**
 * Setzt den Sync-Status.
 */
export function setSyncStatus(status: SyncStatus, error?: string): void {
  syncState.value = {
    ...syncState.value,
    status,
    error: error ?? null,
  };
}

/**
 * Aktualisiert die Anzahl ausstehender Operations.
 */
export function setPendingCount(count: number): void {
  syncState.value = {
    ...syncState.value,
    pendingCount: count,
  };
}

/**
 * Markiert Sync als erfolgreich.
 */
export function setSyncSuccess(): void {
  syncState.value = {
    status: 'idle',
    pendingCount: 0,
    lastSyncAt: new Date().toISOString(),
    error: null,
  };
}

// =============================================================================
// Derived Sync State
// =============================================================================

/** Gibt es ungesyncte Änderungen? */
export const hasUnsyncedChanges = computed(() =>
  syncState.value.pendingCount > 0
);

/** Ist gerade ein Sync aktiv? */
export const isSyncing = computed(() =>
  syncState.value.status === 'syncing'
);

/** Ist die App offline? */
export const isOffline = computed(() =>
  syncState.value.status === 'offline'
);

/** Gibt es einen Sync-Fehler? */
export const hasSyncError = computed(() =>
  syncState.value.status === 'error'
);

// =============================================================================
// Current Context (aktuelle Ansicht)
// =============================================================================

/** Aktuell geladenes TimeSheet */
export const currentTimeSheet = signal<TimeSheet | null>(null);

/** Entries des aktuellen TimeSheets */
export const currentEntries = signal<TimeEntry[]>([]);

/** Aktueller User-Context */
export const currentUserId = signal<string | null>(null);

/** Aktuelle Tenant-Config */
export const tenantConfig = signal<TenantConfig | null>(null);

// =============================================================================
// Team-Daten (für Teamerfasser)
// =============================================================================

/** Mitarbeiter des aktuellen Teams */
export const teamEmployees = signal<Employee[]>([]);

/** TimeSheets des aktuellen Tages */
export const todayTimeSheets = signal<TimeSheet[]>([]);

// =============================================================================
// Derived Current State
// =============================================================================

/** Kann das aktuelle TimeSheet bearbeitet werden? */
export const canEditCurrentSheet = computed(() => {
  const sheet = currentTimeSheet.value;
  if (!sheet) return false;

  // Nur DRAFT kann bearbeitet werden
  // Oder wenn nicht TA-locked
  return sheet.status === 'DRAFT' && !sheet.taLocked;
});

/** Kann Tagesabschluss gemacht werden? */
export const canSubmitCurrentSheet = computed(() => {
  const sheet = currentTimeSheet.value;
  const sync = syncState.value;

  if (!sheet) return false;

  // Muss DRAFT sein
  if (sheet.status !== 'DRAFT') return false;

  // Muss online sein
  if (sync.status === 'offline') return false;

  // Keine pending Operations
  if (sync.pendingCount > 0) return false;

  return true;
});

/** Anzahl Entries im aktuellen Sheet */
export const currentEntryCount = computed(() =>
  currentEntries.value.length
);

// =============================================================================
// UI State
// =============================================================================

/** Wird gerade ein Eintrag erstellt? */
export const isCreatingEntry = signal(false);

/** Wird gerade submitted? */
export const isSubmitting = signal(false);

/** Toast/Notification Message */
export const toastMessage = signal<ToastMessage | null>(null);

export interface ToastMessage {
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

/**
 * Zeigt eine Toast-Nachricht an.
 */
export function showToast(
  type: ToastMessage['type'],
  message: string,
  duration = 3000
): void {
  toastMessage.value = { type, message, duration };

  // Auto-hide
  if (duration > 0) {
    setTimeout(() => {
      toastMessage.value = null;
    }, duration);
  }
}

// =============================================================================
// Reset Functions
// =============================================================================

/**
 * Setzt den aktuellen Context zurueck.
 */
export function resetCurrentContext(): void {
  currentTimeSheet.value = null;
  currentEntries.value = [];
}

/**
 * Setzt alle UI-States zurueck.
 */
export function resetUIState(): void {
  isCreatingEntry.value = false;
  isSubmitting.value = false;
  toastMessage.value = null;
}

/**
 * Setzt den gesamten Store zurueck (z.B. bei Logout).
 */
export function resetStore(): void {
  resetCurrentContext();
  resetUIState();

  syncState.value = {
    status: 'idle',
    pendingCount: 0,
    lastSyncAt: null,
    error: null,
  };

  currentUserId.value = null;
  tenantConfig.value = null;
  teamEmployees.value = [];
  todayTimeSheets.value = [];
}
